import { useEffect, useState } from 'react';
import { useFractionGameLogic } from '../../hooks/useFractionGameLogic';
import { useGameTimer } from '../../hooks/useGameTimer';
import { GameControls } from '../Game/GameControls';
import { GameStats } from '../Game/GameStats';
import { GameTimer } from '../Game/GameTimer';
import { FractionGameDisplay } from './FractionGameDisplay';
import { GameStartPage } from '../GameStartPage';

export const FractionGame: React.FC = () => {
  const [hasStarted, setHasStarted] = useState(false);
  const {
    currentQuestion,
    showAnswer,
    skipQuestion,
    previousQuestion,
    nextQuestion,
    stats,
    isBlocked,
  } = useFractionGameLogic();

  const {
    time,
    isRunning,
    questionsInCurrentSession,
    remainingTime,
    toggleTimer,
    resetTimer,
    setTimerDuration,
    incrementQuestions,
    getQuestionsPerMinute,
  } = useGameTimer();

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // If the focused element is an interactive control, don't handle to avoid double actions
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName?.toLowerCase();
        const isInteractive = ['input', 'textarea', 'button', 'select', 'a'].includes(tag) ||
          !!target.getAttribute?.('role')?.match(/button|link/);
        if (isInteractive) return;
      }

      if (e.code === 'ArrowLeft') {
        previousQuestion();
      } else if (e.code === 'ArrowRight' || e.code === 'Space') {
        e.preventDefault();
        e.stopPropagation();
        handleNextQuestion();
      } else if (e.code === 'KeyS') {
        skipQuestion();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [previousQuestion, nextQuestion, skipQuestion, incrementQuestions, showAnswer]);

  const handleNextQuestion = () => {
    if (!showAnswer) {
      incrementQuestions();
    }
    nextQuestion();
  };

  if (!hasStarted) {
    return <GameStartPage gameTitle="Fractions & Percentages" onStart={() => setHasStarted(true)} />;
  }

  return (
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-lg rounded-2xl shadow-xl p-8 border border-gray-100 dark:border-gray-700 transition-colors duration-200">
          <GameTimer
            time={time}
            isRunning={isRunning}
            remainingTime={remainingTime}
            onToggle={toggleTimer}
            onReset={resetTimer}
            onSetDuration={setTimerDuration}
          />

          <div className="mb-8">
            <h3 className="text-xl font-bold mb-4 text-gray-800 dark:text-gray-200">Instructions</h3>
            <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                <li>Questions alternate between converting fractions to percentages and vice versa</li>
                <li>For percentage answers, use up to 2 decimal places (e.g., 33.33%)</li>
                <li>For fraction answers, use simplified fractions (e.g., 1/3 instead of 2/6)</li>
                <li>Decimal equivalents are shown with the answer for reference</li>
              </ul>
            </div>
          </div>

          <FractionGameDisplay
            question={currentQuestion.question}
            answer={currentQuestion.answer}
            decimal={currentQuestion.decimal}
            showAnswer={showAnswer}
            isBlocked={isBlocked}
            type={currentQuestion.type}
          />

          <GameControls
            onPrevious={previousQuestion}
            onSkip={skipQuestion}
            onNext={handleNextQuestion}
          />

          <GameStats
            stats={stats}
            questionsInCurrentSession={questionsInCurrentSession}
            questionsPerMinute={Number(getQuestionsPerMinute())}
            isTimerRunning={isRunning}
          />
        </div>
      </div>
    );
};